import {PageHeader} from "../components/PageHeader";
import {FixtureBoundary} from "../components/FixtureBoundary";
import {auditEvents, evidence, workers, workPackages} from "../data/controlPlane";
import {useAuth} from "../auth/AuthContext";
import type {OperationsStatus} from "../api/client";
import type {PropsWithChildren} from "react";
export {WorkPackageAuthoring as WorkPackages} from "./WorkPackageAuthoring";
export {ApprovalInbox as Approvals} from "./ApprovalInbox";
export {ExecutionPlanning} from "./ExecutionPlanning";

export function Overview({status}: {status: OperationsStatus}) {
  return (
    <>
      <PageHeader eyebrow="Control plane / Overview" title="Factory readiness"
        description="Plan, simulate, inspect, and approve software factory work without crossing an external mutation boundary."
        aside={<div className="safety-seal"><span>0 external writes</span><strong>Boundary intact</strong></div>} />
      <section className="metric-grid" aria-label="Factory summary">
        <Metric value="3" label="Open work packages" note="1 awaiting approval" />
        <Metric value="3 / 3" label="Simulated workers" note="Evidence complete" />
        <Metric value="1" label="Policy denial" note="Expected outcome" />
        <Metric
          value={status.health}
          label="Runtime health"
          note={`${status.dependencies.filter(dependency => dependency.state !== "UP").length} dependencies require attention`}
        />
      </section>
      <div className="split-grid">
        <section className="panel">
          <PanelTitle title="Work in motion" meta="Read-only operational view" />
          <WorkTable />
        </section>
        <section className="panel decision-panel">
          <PanelTitle title="Latest decision" meta="Policy engine" />
          <p className="decision-code">DENY</p>
          <h2>External mutation stopped</h2>
          <p>The human gate completed, then the Workbench 0.1 default-deny policy prevented <code>gitlab.branch.create</code>.</p>
          <dl className="detail-list">
            <div><dt>Policy</dt><dd>workbench-0.1-default-deny</dd></div>
            <div><dt>Side effects</dt><dd>None</dd></div>
            <div><dt>Evidence</dt><dd>3 verified records</dd></div>
          </dl>
        </section>
      </div>
    </>
  );
}

export function Portfolio() {
  return <DataPage eyebrow="Planning" title="Portfolio planning" description="Trace products and capabilities to governed work packages.">
    <div className="map-flow">
      {["ASF Platform", "Control Plane", "Operational governance", "WP-DEMO-001"].map((item, index) =>
        <div key={item} className="map-node"><small>Level {index + 1}</small><strong>{item}</strong></div>)}
    </div>
  </DataPage>;
}

export function Products() {
  return <DataPage eyebrow="Planning" title="Products" description="Products turn portfolio intent into governed capability roadmaps.">
    <div className="card-grid">
      {["ASF Workbench", "ASF Cockpit", "Inteacc Platform"].map((name, index) =>
        <article className="worker-card" key={name}><span className="worker-avatar" aria-hidden="true">P{index + 1}</span>
          <div><h2>{name}</h2><p>{index === 0 ? "Control-plane foundation" : "Compatibility boundary"}</p></div>
          <span className={`badge ${index === 0 ? "safe" : ""}`}>{index === 0 ? "In foundation" : "Planned"}</span>
        </article>)}
    </div>
  </DataPage>;
}

export function Capabilities() {
  return <DataPage eyebrow="Planning" title="Capabilities" description="Trace operational capabilities, blocked reasons, dependencies, and approval state.">
    <div className="map-flow">
      {["Portfolio governance", "Workspace control", "Worker simulation", "Evidence & approval"].map((item, index) =>
        <div key={item} className="map-node"><small>{index === 3 ? "Acceptance gate" : "Ready"}</small><strong>{item}</strong></div>)}
    </div>
    <Callout title="Blocked capability" text="Controlled GitLab writes remain inactive pending a separate identity and policy work package." />
  </DataPage>;
}

export function Workspaces() {
  return <DataPage eyebrow="Execution context" title="Workspaces" description="Lifecycle-controlled contexts derived from approved work.">
    <Timeline labels={["Draft", "Validated", "Awaiting activation", "Active", "Awaiting review", "Accepted"]} />
    <Callout title="Workspace wb-demo-001" text="Terminal state: ACCEPTED · version 5 · external mutation denied · synthetic provider only" />
  </DataPage>;
}

export function Orchestration() {
  return <DataPage eyebrow="Deterministic engine" title="Orchestration" description="A stable DAG makes task ordering, dependencies, and evidence reproducible.">
    <div className="dag" aria-label="Three sequential orchestration tasks">
      {workers.map((worker, index) => <div className="dag-stage" key={worker.key}>
        <span>{String(index + 1).padStart(2, "0")}</span><strong>{worker.stage}</strong><small>{worker.key}</small>
      </div>)}
    </div>
    <Callout title="Plan integrity" text="SHA-256 6c02ae387b1e…7bc5 · stable UUID · cycle checks passed" />
  </DataPage>;
}

export function FactoryFloor() {
  const {user} = useAuth();
  return <DataPage eyebrow="Live simulation" title="Factory floor" description="Follow every simulated worker, handoff, gate, and denial in one operational surface.">
    <div className="factory-track">{workers.map((worker, index) => <article key={worker.key} className="factory-stage">
      <div className="stage-index">{index + 1}</div><p>{worker.stage}</p><h2>{worker.name}</h2>
      <span className="badge safe">Complete</span><small>{worker.duration} · no runtime capability</small>
    </article>)}</div>
    <article className="gate-strip"><div className="gate-icon">H</div><div><p>Human approval gate</p><strong>Approved by {user?.displayName}</strong></div><span>Then</span><div className="deny-mark">Mutation denied</div></article>
  </DataPage>;
}

export function Evidence() {
  return <DataPage eyebrow="Assurance" title="Evidence ledger" description="Checksummed records establish what was planned, simulated, approved, and denied.">
    <table><thead><tr><th>Evidence</th><th>Source</th><th>Digest</th><th>Integrity</th></tr></thead>
      <tbody>{evidence.map(item => <tr key={item.type}><td>{item.type}</td><td>{item.source}</td><td><code>{item.digest}</code></td><td><span className="badge safe">{item.result}</span></td></tr>)}</tbody></table>
  </DataPage>;
}

export function Audit() {
  const {user} = useAuth();
  return <DataPage eyebrow="Accountability" title="Audit trail" description="Actor, action, decision, correlation, and causation remain queryable.">
    <ol className="audit-list">{auditEvents.map(event => <li key={event.action}><time>{event.time}</time><div><strong>{event.action}</strong><span>{event.actor === "authenticated-subject" ? user?.username : event.actor}</span></div><b className={event.outcome === "DENY" ? "deny-text" : ""}>{event.outcome}</b></li>)}</ol>
  </DataPage>;
}

export function Operations({status}: {status: OperationsStatus}) {
  return <DataPage liveSource eyebrow="Reliability" title="Operations" description="Health, incidents, dependencies, and safety posture for the control plane.">
    <section className="metric-grid"><Metric value={status.health} label="Application" note="Observed dependency aggregate" /><Metric value={component(status, "postgresql")} label="PostgreSQL" note="Live database probe" /><Metric value={String(status.activeIncidents.length)} label="Incidents" note="Current status response" /><Metric value={status.environment} label="Environment" note="Non-production" /></section>
    <table><thead><tr><th>Component</th><th>State</th><th>Observation</th></tr></thead>
      <tbody>{status.dependencies.map(item => <tr key={item.component}><td>{item.component}</td><td><span className={`badge ${item.state === "UP" ? "safe" : "deny"}`}>{item.state}</span></td><td>{item.detail}</td></tr>)}</tbody></table>
    <Callout title="Runtime hardening" text="Non-root · read-only root filesystem · all Linux capabilities dropped · no-new-privileges" />
  </DataPage>;
}

function component(status: OperationsStatus, name: string): string {
  return status.dependencies.find(item => item.component === name)?.state ?? "DOWN";
}

export function Settings({status}: {status: OperationsStatus}) {
  return <DataPage eyebrow="Configuration" title="Settings" description="Visible policy and identity configuration; secrets are never rendered.">
    <dl className="settings-list">
      <div><dt>OIDC flow</dt><dd>Authorization Code + PKCE</dd></div><div><dt>Tenant isolation</dt><dd>Required on every durable record</dd></div>
      <div><dt>GitLab mutation authority</dt><dd>Not exposed by the status API</dd></div>
      <div><dt>Execution architecture</dt><dd>{status.execution.mode}</dd></div>
      <div><dt>Execution lane</dt><dd>{status.execution.laneRequired
        ? `${status.execution.laneIdentity ?? "Unidentified"} · ${status.execution.laneStatus}`
        : "Not required"}</dd></div>
    </dl>
  </DataPage>;
}

export function CockpitCompatibility() {
  return <DataPage eyebrow="Formal boundary" title="Cockpit convergence" description="Compatibility and migration boundary for Cockpit capabilities inside the single Inteacc Software Factory.">
    <Callout title="Authoritative Cockpit reference" text="6a3cbb6b89ef8cdda22b75b5cd43520016fe4d69" />
    <dl className="settings-list"><div><dt>Integration state</dt><dd>ROUTE_CONVERGENCE_ACTIVE</dd></div><div><dt>Duplicate authority imported</dt><dd>No</dd></div><div><dt>Activation</dt><dd>Single Software Factory convergence</dd></div></dl>
  </DataPage>;
}

function DataPage({eyebrow, title, description, liveSource = false, children}: PropsWithChildren<{
  eyebrow: string;
  title: string;
  description: string;
  liveSource?: boolean;
}>) {
  const content = <section className="panel page-panel">{children}</section>;
  return <><PageHeader eyebrow={eyebrow} title={title} description={description} />
    {liveSource ? content : <FixtureBoundary source="frontend/src/data/controlPlane.ts or page-local static content"
      replacement="the scoped live read model registered for its delivery wave">{content}</FixtureBoundary>}
  </>;
}
function Metric({value, label, note}: {value: string; label: string; note: string}) {
  return <article className="metric"><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}
function PanelTitle({title, meta}: {title: string; meta: string}) {
  return <div className="panel-title"><h2>{title}</h2><span>{meta}</span></div>;
}
function WorkTable() {
  return <div className="table-scroll" tabIndex={0} aria-label="Scrollable work package table"><table><thead><tr><th>Work package</th><th>Owner</th><th>Status</th><th>Risk</th></tr></thead>
    <tbody>{workPackages.map(item => <tr key={item.key}><td><strong>{item.key}</strong><span>{item.title}</span></td><td>{item.owner}</td><td><span className={`badge ${item.status === "Approved" ? "safe" : item.status.startsWith("Awaiting") ? "amber" : ""}`}>{item.status}</span></td><td>{item.risk}</td></tr>)}</tbody></table></div>;
}
function Timeline({labels}: {labels: string[]}) {
  return <ol className="timeline">{labels.map((label, index) => <li key={label}><span>{index + 1}</span><strong>{label}</strong></li>)}</ol>;
}
function Callout({title, text}: {title: string; text: string}) {
  return <aside className="callout"><span aria-hidden="true">✓</span><div><strong>{title}</strong><p>{text}</p></div></aside>;
}
